import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  LinearProgress,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

const BORDER_COLORS: string[] = ['#ffc107', '#17a2b8', '#6f42c1', '#28a745', '#dc3545', '#fd7e14'];

interface Meal {
  id?: string | number;
  name?: string;
  type?: string;
  icon?: string;
  time?: string;
  menu?: string;
  target?: number;
  prepared?: number;
  scheduled?: number;
  borderColor?: string;
  mealPlanName?: string;
  isPublished?: boolean;
  isOperational?: boolean;
  productionStatus?: string;
}

const MealSchedule: React.FC = () => {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduleMessage, setScheduleMessage] = useState<string | null>(null);
  const [isOperational, setIsOperational] = useState<boolean>(true);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setScheduleMessage(null);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const today = new Date().toISOString().slice(0, 10);
      const resp = await fetch(`${API_BASE}/kitchen-ops/meal-planning/today?date=${today}&includeDrafts=true`, { headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const list = data.data ?? data;
      setScheduleMessage(data.message ?? null);
      setIsOperational(data.isOperational !== false);
      setMeals(Array.isArray(list) ? list : []);
    } catch (e) {
      setError((e as Error).message || 'Failed to load meal schedule');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const calculateProgress = (prepared: number, target: number): string => {
    if (!target) return '0';
    return Math.min(100, ((prepared / target) * 100)).toFixed(1);
  };

  const getMealSlotStyles = (borderColor: string) => ({
    backgroundColor: '#f8f9fa',
    p: 2,
    borderRadius: 2,
    borderLeft: `4px solid ${borderColor}`,
  });

  return (
    <Card sx={{ mb: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.15)', borderRadius: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Today's Meal Schedule
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!error && scheduleMessage && (
          <Alert severity={isOperational ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {scheduleMessage}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : meals.length === 0 ? (
          <Alert severity="info">No meal schedule data available from the API.</Alert>
        ) : (
          <Grid container spacing={3}>
            {meals.map((meal, idx) => (
              <Grid item xs={12} md={4} key={meal.id ?? idx}>
                <Box sx={getMealSlotStyles(meal.borderColor ?? BORDER_COLORS[idx % BORDER_COLORS.length])}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                    {meal.icon ? `${meal.icon} ` : ''}{meal.name ?? meal.type} {meal.time ? `(${meal.time})` : ''}
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {meal.menu && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Menu:</strong> {meal.menu}
                      </Typography>
                    )}
                    {meal.mealPlanName && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Plan:</strong> {meal.mealPlanName}
                        {meal.isPublished === false ? ' (draft)' : ''}
                      </Typography>
                    )}
                    {meal.target != null && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Target:</strong> {meal.target.toLocaleString()} pupils
                      </Typography>
                    )}
                    {meal.scheduled != null && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Scheduled:</strong> {meal.scheduled.toLocaleString()} portions
                      </Typography>
                    )}
                    {meal.prepared != null && meal.prepared > 0 && (
                      <Typography variant="body2">
                        <strong>Prepared:</strong>{' '}
                        {meal.prepared.toLocaleString()} portions
                      </Typography>
                    )}
                  </Box>
                  {meal.target != null && meal.prepared != null && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={parseFloat(calculateProgress(meal.prepared, meal.target))}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          backgroundColor: '#e9ecef',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #28a745, #20c997)',
                            borderRadius: 1,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {calculateProgress(meal.prepared, meal.target)}% Complete
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default MealSchedule;
